import FormattedMessage from 'I18n/components/FormattedMessage';
import { useRef } from 'react';
import { useCreateMachine } from 'fsm';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Text } from 'foundations-components/components/Text';
import { getParamDefaultsFromSteps } from '../../../stepRenderer';
import { isOAuth } from '../../utils/oauthHelpers';
import { getLowLevelAuthFlowMachineDefinition } from './LowLevelAuthFlowMachineDefinition';
import { LowLevelAuthFlowMachineContext } from './LowLevelAuthFlowMachineContext';
import { InlineAuthFlowLayout } from './InlineAuthFlowLayout';
import { WizardAuthFlowLayout } from './WizardAuthFlowLayout';
import { LowLevelAuthErrorAlert } from './LowLevelAuthErrorAlert';
import { useAuthFlowInspector } from './hooks/useAuthFlowInspector';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function resolveInitialStepIndex({
  inline,
  introScreen,
  authFlowType,
  hasSteps
}) {
  if (inline) {
    return -1;
  }
  if (introScreen || !hasSteps && isOAuth(authFlowType)) {
    return -1;
  }
  return 0;
}
export function LowLevelAuthFlowView({
  isModal,
  isReauth,
  portalId,
  authConfig,
  onComplete,
  onCancel,
  introScreen,
  successScreen,
  errorProcessor,
  onError,
  debug = false
}) {
  const {
    appName,
    authFlow
  } = authConfig;
  const {
    inline = false
  } = authFlow;
  const paramsRef = useRef(getParamDefaultsFromSteps(authFlow.steps));
  const initialStepIndex = resolveInitialStepIndex({
    inline,
    introScreen,
    authFlowType: authFlow.type,
    hasSteps: authFlow.steps.length > 0
  });
  const machineDefinition = getLowLevelAuthFlowMachineDefinition();
  const inspector = useAuthFlowInspector(debug, appName);
  const machine = useCreateMachine(machineDefinition, {
    initialContext: {
      portalId,
      authConfig,
      params: paramsRef.current,
      stepIndex: initialStepIndex,
      initialStepIndex,
      authState: {
        status: 'pending'
      },
      authError: undefined,
      onComplete,
      onError,
      authProcessStatus: 'idle',
      pollingCount: 0,
      oAuthState: '',
      oAuthWindow: null
    },
    inspector: inspector || undefined
  });
  const {
    state,
    send
  } = machine;
  const {
    authState,
    authError
  } = state.context;
  const flowErrorContext = {
    appId: authConfig.appId,
    appName,
    onCancel,
    onReset: () => send({
      type: 'CLEAR_ERROR'
    })
  };
  const customErrorContext = Object.assign({}, flowErrorContext, {
    onReset: () => send({
      type: 'RESET_CUSTOM_ERROR'
    })
  });
  if (state.matches('customError') && authError) {
    return /*#__PURE__*/_jsx(LowLevelAuthErrorAlert, {
      appName: appName,
      error: authError,
      context: customErrorContext,
      processError: errorProcessor
    });
  }
  if (state.matches('success') && successScreen && authState.status === 'succeeded') {
    return successScreen({
      appName,
      authResponse: authState.authResponse,
      onCancel
    });
  }
  const header = isModal ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h2",
      children: isReauth ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.modal.reconnectDialogHeader",
        options: {
          appName
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.modal.dialogHeader",
        options: {
          appName
        }
      })
    }), /*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onCancel
    })]
  }) : undefined;
  return /*#__PURE__*/_jsx(LowLevelAuthFlowMachineContext.Provider, {
    value: machine,
    children: inline ? /*#__PURE__*/_jsx(InlineAuthFlowLayout, {
      isModal: isModal,
      header: header,
      authFlowType: authFlow.type,
      introScreen: introScreen,
      processError: errorProcessor,
      errorContext: flowErrorContext
    }) : /*#__PURE__*/_jsx(WizardAuthFlowLayout, {
      isModal: isModal,
      header: header,
      isReauth: isReauth,
      authFlowType: authFlow.type,
      introScreen: introScreen,
      processError: errorProcessor,
      errorContext: flowErrorContext
    })
  });
}